import React, { useEffect, useRef, useState } from 'react';

import InputField from '../InputField';
import './index.css';

import SingleDayPicker from '@React_UI_Lib/SingleDayPicker';
import { Modal } from '../CommonHeaderReact/src/components/CommonModal/CommonModal';
import { formatDate } from '../../utils/timeUtils';
import { FieldConfig, FormFields } from './config';
import { assistFormApiCall } from '../../cabsAPIs';
import { GENERIC_ERROR_TEXT } from '../../constants/errors';
// import { logRttPhoneNumberErrorRequestBuilder } from '../../logRttRequestBuilder';
import { isMobile } from '../../utils/commonUtils';
import { getAppEnv } from '../../../config/env';
import InboundCallBoxInAsstForm from '../InboundCall/InboundCallBoxAsstForm';
const FORM_DATA_STORAG_TTL = 24 * 60 * 60 * 1000;
const ASSISTED_FORM_DATA_LS = 'ASSISTED_FORM_DATA_LS';
const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

interface AssistModalProps {
  setShowModal: (val: any) => void;
  sourceCity?: string;
  destinationCity?: string;
  trackTemplateEvent?: (eventWithValToTrack: string) => void;
  showWhatsAppForAssistedForm?: boolean;
  customerCareNumber?: string;
  assistantWidgetText?: string;
  showCallUsForAssistedForm?: boolean;
  onWhatsAppIconClick?: () => void;
  onCallUsIconClick?: () => void;
  showInboundNumberOnAssistedForm?: boolean;
  inboundCallNumber?: string;
  inboundCallText?: string;
}

const initFormData = {};
for (const key in FieldConfig) {
  const val = FieldConfig[key];
  initFormData[val?.key] = {
    val: val?.defaultValue,
    hasError: false
  };
}

const AssistModal: React.FC<AssistModalProps> = ({
  setShowModal,
  sourceCity,
  destinationCity,
  trackTemplateEvent,
  showWhatsAppForAssistedForm,
  customerCareNumber,
  assistantWidgetText,
  onWhatsAppIconClick,
  onCallUsIconClick,
  showCallUsForAssistedForm,
  showInboundNumberOnAssistedForm,
  inboundCallNumber,
  inboundCallText
}) => {
  const [formSubmit, setFormSubmit] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [formData, setFormData] = useState<any>(initFormData);
  const formDataRef = useRef(formData);
  const [isLoadingAPI, setIsLoadingAPI] = useState(false);
  const [formApiError, setFormApiError] = useState(false);
  const [formApiErrorText, setFormApiErrorText] = useState('');

  useEffect(() => {
    // Storing formData on formDataRef to be stored while unmounting of AssistModal.
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (window?.localStorage) {
      const cachedData = localStorage.getItem(ASSISTED_FORM_DATA_LS);
      let cachedFormData: any = null;
      try {
        cachedFormData = cachedData != null ? JSON.parse(cachedData) : null;
      } catch (err) {
        console.error('Error in parsing saved form data');
      }
      if (
        cachedFormData &&
        cachedFormData.expiry &&
        cachedFormData.expiry > new Date().getTime()
      ) {
        setFormData(prevData => cachedFormData);
      } else {
        //Set pre-available source and destination locations
        setFormData(prevData => ({
          ...prevData,
          [FormFields?.FROM]: {
            ...prevData[FormFields?.FROM],
            val: sourceCity
          },
          [FormFields?.TO]: {
            ...prevData[FormFields?.TO],
            val: destinationCity
          }
        }));
      }
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
      setAssistedFromInfoWithExpiry();
    };
  }, []);

  const setAssistedFromInfoWithExpiry = () => {
    try {
      window?.localStorage &&
        localStorage.setItem(
          ASSISTED_FORM_DATA_LS,
          JSON.stringify({
            ...formDataRef.current,
            expiry: new Date().getTime() + FORM_DATA_STORAG_TTL
          })
        );
    } catch (err) {
      console.error(err);
    }
  };

  const onModalClose = () => {
    //TODO: check why setTimeout is required here?
    setTimeout(() => {
      setShowDateModal(false);
    }, 10);
  };

  const onInputFieldChanged = (val, key, callbackFn) => {
    const validators = FieldConfig?.[key]?.validators;
    let fieldHasError = false,
      fieldErrorText = '';

    for (let i = 0; i < (validators || []).length; i++) {
      const { hasError, errorText } = validators?.[i](val);
      if (hasError) {
        fieldHasError = hasError;
        fieldErrorText = errorText;
        break;
      }
    }

    setFormData(prevData => ({
      ...prevData,
      [key]: {
        ...prevData[key],
        val,
        hasError: fieldHasError,
        errorText: fieldErrorText
      }
    }));
    callbackFn?.();
  };

  const onFormSubmitted = () => {
    trackTemplateEvent?.('Cab_SEO_Assisted_Form_Submitted');
    //check errors in the form
    const { formHasErrors, firstFieldWithError } = validateFieldErrors?.();
    if (formHasErrors) {
      trackTemplateEvent?.(
        `Cab_SEO_Assisted_Form_Submitted_Validation_Error_${firstFieldWithError}`
      );

      if (firstFieldWithError === FormFields.MOBILE_NUMBER) {
        logRttPhoneNumberErrorRequestBuilder(formData.mobileNumber?.val);
      }

      return;
    }
    setIsLoadingAPI(true);
    formSubmitCall();
  };

  const formSubmitCall = async () => {
    const {
      from,
      to,
      mobileNumber,
      fullName,
      pickUpDate,
      specialRequest
    } = formData;
    const assistedFormRequest = {
      source: from?.val,
      destination: to?.val || '',
      travel_date: pickUpDate?.val?.toLocaleString().substring(0, 10),
      name: fullName?.val || '',
      mobile: mobileNumber?.val,
      special_request: specialRequest?.val,
      type: 'seoAssistedFlowRequest'
    };

    try {
      const response = await assistFormApiCall(assistedFormRequest);
      //Catch errors coming in successful api response
      if (response['errors']) {
        showErrorAndSubmitForm(
          formData,
          response?.errors?.error_list[0]?.message
        );
      } else {
        trackTemplateEvent?.('Cab_SEO_Assisted_Form_Submitted_Success');
        setFormSubmit(true);
        if (window?.localStorage) {
          localStorage.removeItem(ASSISTED_FORM_DATA_LS);
        }
      }
    } catch (error) {
      //Catch api failure errors
      trackTemplateEvent?.('Cab_SEO_Assisted_Form_Submitted_API_error');
      showErrorAndSubmitForm(formData, GENERIC_ERROR_TEXT);
    }
  };

  const showErrorAndSubmitForm = (formData, errorText) => {
    setFormSubmit(true);
    setFormApiError(true);
    setFormApiErrorText(errorText);
  };

  const validateFieldErrors = () => {
    let formHasErrors = false;
    let firstFieldWithError = '';

    for (const field in formData) {
      const currVal = formData[field]?.val;
      const validators = FieldConfig?.[field]?.validators;

      for (let i = 0; i < (validators || []).length; i++) {
        const { hasError, errorText } = validators?.[i](currVal);

        setFormData(prevData => ({
          ...prevData,
          [field]: {
            ...prevData[field],
            hasError,
            errorText
          }
        }));

        formHasErrors = formHasErrors || hasError;
        if (hasError) {
          firstFieldWithError = firstFieldWithError
            ? firstFieldWithError
            : field;
          break;
        }
      }
    }
    return { formHasErrors, firstFieldWithError };
  };

  return (
    <div
      className="modalViewOverlay"
      style={{ height: document.documentElement.scrollHeight }}
    >
      <div className="modalViewOuterContainer">
        <div className="modalViewContainer">
          {!formSubmit ? (
            <div className="modalWrapper assistedModal">
              <div
                className="crossIc"
                onClick={() => {
                  setShowModal(false);
                  trackTemplateEvent?.('Cab_SEO_Assisted_Form_Closed');
                }}
              />
              <p className="modalTitle">We are here to assist you</p>
              <p className="modalSubTitle">
                Just share few details and our agent will reach out
              </p>
              <div className="formContainer">
                <div className="formRow mobCol">
                  <InputField
                    formData={formData}
                    fieldConfig={FieldConfig[FormFields?.FULL_NAME]}
                    onChangeHandler={onInputFieldChanged}
                    onFocusHandler={() =>
                      trackTemplateEvent?.('Cab_SEO_Assisted_Form_Name_Clicked')
                    }
                  />
                  <div className="inputSeperator" />
                  <InputField
                    formData={formData}
                    fieldConfig={FieldConfig[FormFields?.MOBILE_NUMBER]}
                    customClassName="numberInput"
                    onChangeHandler={onInputFieldChanged}
                    onClickHandler={() =>
                      trackTemplateEvent?.(
                        'Cab_SEO_Assisted_Form_Phone_Clicked'
                      )
                    }
                  />
                </div>
                <div className="formRow">
                  <InputField
                    formData={formData}
                    fieldConfig={FieldConfig[FormFields?.FROM]}
                    onChangeHandler={onInputFieldChanged}
                  />
                  <div className="inputSeperator" />
                  <InputField
                    formData={formData}
                    fieldConfig={FieldConfig[FormFields?.TO]}
                    onChangeHandler={onInputFieldChanged}
                  />
                </div>
                <div className="formRow" onClick={() => setShowDateModal(true)}>
                  <InputField
                    formData={formData}
                    fieldConfig={FieldConfig[FormFields?.PICKUP_DATE]}
                  />
                  <Modal
                    show={showDateModal}
                    modalMainCustomClass="modalMainDatePicker"
                    hideTopSection={true}
                    handleClose={onModalClose}
                  >
                    <SingleDayPicker
                      onDaySelect={day =>
                        onInputFieldChanged(
                          formatDate(day),
                          FormFields?.PICKUP_DATE,
                          () => setShowDateModal(false)
                        )
                      }
                      disabledPrevDates={true}
                    />
                  </Modal>
                </div>
                <div className="formRow">
                  <InputField
                    formData={formData}
                    fieldConfig={FieldConfig[FormFields?.SPECIAL_REQUEST]}
                    onChangeHandler={onInputFieldChanged}
                  />
                </div>
              </div>
              <div className="formBtnCtaWrap" onClick={onFormSubmitted}>
                <button
                  className={`loader-button ${isLoadingAPI ? 'loading' : ''}`}
                >
                  <span className="loader"></span>
                  <span className="button-text">SUBMIT</span>
                </button>
              </div>
              {isMobile() &&
                (showWhatsAppForAssistedForm || showCallUsForAssistedForm) && (
                  <div className="form-btm-area">
                    <div className="seperation-section">
                      <div className="border-seperator"></div>
                      <div className="or-text">OR</div>
                      <div className="border-seperator"></div>
                    </div>
                    <p className="callMsgTxt">{assistantWidgetText}</p>
                    <div className="bottomConnectWrapper">
                      {showCallUsForAssistedForm && customerCareNumber && (
                        <button
                          className="btm-prime-buttons outline-btn-call"
                          onClick={() => {
                            trackTemplateEvent?.(
                              'Cab_SEO_Assisted_Form_Call_Clicked'
                            );
                            onCallUsIconClick?.();
                          }}
                        >
                          <img
                            src={`${PUBLIC_URL}/static/public/call.png`}
                            className="callOption"
                          />
                          Call Us
                        </button>
                      )}
                      {showWhatsAppForAssistedForm && (
                        <button
                          className="btm-prime-buttons solid-btn-whatsapp"
                          onClick={() => {
                            trackTemplateEvent?.(
                              'Cab_SEO_Assisted_Form_WhatsApp_Clicked'
                            );
                            onWhatsAppIconClick?.();
                          }}
                        >
                          <img
                            src={`${PUBLIC_URL}/static/public/whatsApp.png`}
                            className="whatsAppOption"
                          />
                          WhatsApp Chat
                        </button>
                      )}
                    </div>
                  </div>
                )}
              {!isMobile() && showInboundNumberOnAssistedForm && (
                <div className="form-btm-area">
                  <div className="seperation-section">
                    <div className="border-seperator"></div>
                    <div className="or-text">OR</div>
                    <div className="border-seperator"></div>
                  </div>
                  <InboundCallBoxInAsstForm
                    inboundCallText={inboundCallText}
                    inboundCallNumber={inboundCallNumber}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="submittedSection">
              <div
                className="crossIc"
                onClick={() => {
                  setShowModal(false);
                  trackTemplateEvent?.('Cab_SEO_Assisted_Form_Closed');
                }}
              />
              {formApiError ? (
                <>
                  <div className="errorIc" />
                  <p className="submittedTitle">{formApiErrorText}</p>
                  <span
                    className="reopenFormCTA"
                    onClick={() => {
                      setFormSubmit(false);
                      setFormApiError(false);
                      setIsLoadingAPI(false);
                    }}
                  >
                    TRY AGAIN
                  </span>
                </>
              ) : (
                <>
                  <div className="greenCheckIc" />
                  <p className="submittedTitle">We’ve received your details</p>
                  <p className="submittedSubTitle">
                    Our agent will call you shortly
                  </p>
                </>
              )}
            </div>
          )}
        </div>
        <div className="arrowDownIc" />
      </div>
      <div
        className="modalBackdrop"
        onClick={() => {
          setShowModal(false);
          trackTemplateEvent?.('Cab_SEO_Assisted_Form_Closed');
        }}
      ></div>
    </div>
  );
};

export default AssistModal;
